import React, { useEffect, useState } from 'react';

import {
    Box,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    Button,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Stack,
    useTheme,
} from '@mui/material';

const CustomTableWithPagination = ({
    columns,
    rows,
    title,
    defaultSortFieldId,
    customRowsPerPage,
    defaultOrder,
    compactPadding = false, // Prop opcional para reducir padding en modo compacto
}) => {
    const [order, setOrder] = useState(defaultOrder);
    const [orderBy, setOrderBy] = useState(defaultSortFieldId);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(customRowsPerPage || 10);

    const theme = useTheme();

    const handleRequestSort = (property) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    const handleChangePage = (newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(event.target.value);
        setPage(0);
    };

    const sortedRows = rows?.slice()?.sort((a, b) => {
        const column = columns.find((col) => col.id === orderBy) || columns[0];

        if (column.sortFunction) {
            const result = column.sortFunction(a, b);
            return order === 'asc' ? result : -result;
        }

        // Fallback para ordenamiento por propiedad directa
        const aValue = a[orderBy];
        const bValue = b[orderBy];

        if (aValue < bValue) {
            return order === 'asc' ? -1 : 1;
        }
        if (aValue > bValue) {
            return order === 'asc' ? 1 : -1;
        }
        return 0;
    });

    const paginatedRows = sortedRows?.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

    // Actualizar rowsPerPage cuando customRowsPerPage cambie
    useEffect(() => {
        if (customRowsPerPage) {
            setRowsPerPage(customRowsPerPage);
            setPage(0); // Resetear a la primera página
        }
    }, [customRowsPerPage]);

    useEffect(() => {
        if (!rows || rows.length === 0 || rowsPerPage * page >= rows.length) {
            setPage(0);
        }
    }, [rows, rowsPerPage, page]);

    if (!rows || rows.length === 0) {
        return null;
    }

    return (
        <Box sx={{ width: '100%' }}>
            {title?.length > 1 && (
                <Box
                    sx={{
                        p: 1.5,
                        backgroundColor: 'background.default',
                        borderColor: theme.palette.border.main,
                    }}
                >
                    <Typography variant="h6" color="text.primary">
                        {title}
                    </Typography>
                </Box>
            )}

            <TableContainer
                component={Paper}
                elevation={0}
                sx={{
                    maxWidth: '100%',
                    overflowX: 'auto',
                    position: 'relative',
                    border: `1px solid ${theme.palette.border.light}`,
                    backgroundColor: 'background.default',
                }}
            >
                <Table sx={{ minWidth: 650 }}>
                    <TableHead>
                        <TableRow>
                            {columns.map((column) => {
                                const cellWidth = column.width ? `${column.width}px` : 'auto';
                                return (
                                    <TableCell
                                        key={column.id}
                                        onClick={() => handleRequestSort(column.id)}
                                        sx={{
                                            cursor: 'pointer',
                                            width: cellWidth,
                                            maxWidth: cellWidth,
                                            whiteSpace: 'nowrap',
                                            position: column.fixed ? 'sticky' : 'relative',
                                            left: column.fixed ? 0 : 'auto',
                                            backgroundColor: 'background.card',
                                            zIndex: column.fixed ? 2 : 1,
                                            textTransform: 'uppercase',
                                            fontWeight: 'bold',
                                            fontSize: '0.875rem',
                                            color: 'text.primary',
                                            borderBottom: `2px solid`,
                                            borderColor: theme.palette.border.light,
                                            ...(compactPadding && {
                                                px: { xs: 0.5, sm: 1 },
                                                py: 0.5,
                                            }),
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1,
                                                justifyContent: column.left
                                                    ? 'flex-start'
                                                    : column.right
                                                      ? 'flex-end'
                                                      : 'center',
                                            }}
                                        >
                                            <Typography variant="body2" color="text.primary">
                                                {column.name}
                                            </Typography>
                                            {orderBy === column.id && (
                                                <Typography
                                                    component="span"
                                                    sx={{
                                                        fontSize: '0.75rem',
                                                        color: 'text.secondary',
                                                    }}
                                                >
                                                    {order === 'asc' ? '▲' : '▼'}
                                                </Typography>
                                            )}
                                        </Box>
                                    </TableCell>
                                );
                            })}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {paginatedRows?.map((row, index) => {
                            return (
                                <TableRow
                                    key={row.id || `row-${index}`}
                                    sx={{
                                        height: 80,
                                        '&:hover': {
                                            backgroundColor: 'action.hover',
                                        },
                                    }}
                                >
                                    {columns.map((column) => {
                                        const cellWidth = column.width ? `${column.width}px` : 'auto';
                                        return (
                                            <TableCell
                                                key={column.id}
                                                sx={{
                                                    width: cellWidth,
                                                    maxWidth: cellWidth,
                                                    position: column.fixed ? 'sticky' : 'relative',
                                                    left: column.fixed ? 0 : 'auto',
                                                    backgroundColor: 'background.default',
                                                    zIndex: column.fixed ? 1 : 0,
                                                    textAlign: column.center
                                                        ? 'center'
                                                        : column.right
                                                          ? 'right'
                                                          : 'left',
                                                    color: 'text.primary',
                                                    borderColor: theme.palette.border.light,
                                                    ...(compactPadding && {
                                                        px: { xs: 0.5, sm: 1 },
                                                        py: 0.5,
                                                    }),
                                                }}
                                            >
                                                {column.cell(row, index)}
                                            </TableCell>
                                        );
                                    })}
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>

            <Box sx={{ py: 1 }}>
                <Stack spacing={2}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
                        <Stack direction="row" spacing={1}>
                            <Button
                                variant="outlined"
                                onClick={() => handleChangePage(page - 1)}
                                disabled={page === 0}
                                size="small"
                                sx={{
                                    color: 'text.primary',
                                    borderColor: 'border.main',
                                    '&:hover': {
                                        borderColor: 'primary.main',
                                    },
                                }}
                            >
                                Anterior
                            </Button>
                            <Button
                                variant="outlined"
                                onClick={() => handleChangePage(page + 1)}
                                disabled={page >= Math.ceil(rows.length / rowsPerPage) - 1}
                                size="small"
                                sx={{
                                    color: 'text.primary',
                                    borderColor: 'border.main',
                                    '&:hover': {
                                        borderColor: 'primary.main',
                                    },
                                }}
                            >
                                Siguiente
                            </Button>
                        </Stack>

                        <FormControl size="small" sx={{ minWidth: 120 }}>
                            <InputLabel sx={{ color: 'text.primary' }}>Elementos por página</InputLabel>
                            <Select
                                value={rowsPerPage}
                                label="Elementos por página"
                                onChange={handleChangeRowsPerPage}
                                sx={{
                                    color: 'text.primary',
                                    '& .MuiOutlinedInput-notchedOutline': {
                                        borderColor: 'border.main',
                                    },
                                    '&:hover .MuiOutlinedInput-notchedOutline': {
                                        borderColor: 'primary.main',
                                    },
                                }}
                            >
                                {[5, 10, 20, 50].map((size) => (
                                    <MenuItem key={size} value={size} sx={{ color: 'text.primary' }}>
                                        {size} por página
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Stack>

                    <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                        sx={{ color: 'text.secondary' }}
                    >
                        <Typography variant="body2">
                            {`${page + 1} de ${Math.ceil(rows.length / rowsPerPage)}`}
                        </Typography>
                        <Typography variant="body2">
                            Total de elementos: <strong>{rows.length}</strong>
                        </Typography>
                    </Stack>
                </Stack>
            </Box>
        </Box>
    );
};

export default CustomTableWithPagination;
